import React, { useState } from "react";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const wall = (iso) => DateTime.fromISO(iso, { zone: "utc" });

/**
 * Sticky bottom tray in reschedule mode: the client's own lessons the club booked and
 * never checked out, which the package session can pay for instead of a free time.
 * They sit on their own days in the grid too, but a client moving one lesson should not
 * have to page through weeks to find them. A tap opens the confirm step straight away.
 */
function LessonRescheduleTray({ reschedule, isMobile = false }) {
  // A phone has the filters and the date strip pinned at the top already: the tray
  // starts as one tappable line there, so the times stay in view. Desktop has the room.
  const [listOpen, setListOpen] = useState(!isMobile);
  const own = reschedule?.info?.ownBookings || [];
  if (!reschedule?.active || own.length === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        // Clears BottomNav, which is fixed at bottom 0 below md and paints at zIndex 1000.
        bottom: { xs: "3.5rem", md: 0 },
        left: 0,
        right: 0,
        zIndex: 1000,
        bgcolor: "background.paper",
        borderTop: "1px solid",
        borderColor: "divider",
        boxShadow: "0 -6px 24px rgba(26,26,26,0.08)"
      }}
    >
      <Box sx={{ maxWidth: "60rem", mx: "auto", px: 2, py: 1 }}>
        <Box
          role="button"
          onClick={() => setListOpen((v) => !v)}
          sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
          <EventAvailableIcon sx={{ fontSize: "1.1rem", color: "primary.main" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, lineHeight: 1.2 }}>
              {own.length === 1 ? "A lesson already booked for you" : `${own.length} lessons already booked for you`}
              {!listOpen && isMobile ? " · tap to see" : ""}
            </Typography>
            {(listOpen || !isMobile) && (
              <Typography variant="caption" color="text.secondary">
                Not yet paid — what you paid can cover one of these instead of a new time.
              </Typography>
            )}
          </Box>
          <IconButton size="small" aria-label={listOpen ? "Hide" : "Show"}>
            {listOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
        <Collapse in={listOpen}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, pt: 1, maxHeight: { xs: "22vh", md: "30vh" }, overflowY: "auto" }}>
            {own.map((slot) => (
              <Chip
                key={slot.ownBookingId}
                size="small"
                color="primary"
                variant="outlined"
                icon={<EventAvailableIcon />}
                label={[`${wall(slot.date).toFormat("ccc d LLL")} · ${slot.time}`, slot.trainerName, slot.locationName].filter(Boolean).join(" · ")}
                onClick={() => reschedule.select(slot, slot.date)}
                sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

export default LessonRescheduleTray;
